/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useState } from 'react';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';
import { canAddTarget } from '../../../utils/universalTracker';
import Dashboard, { DashboardRow } from '@g17eco/molecules/dashboard';
import { ViewRadioButtons } from '@features/utr-modal/ViewRadioButtons';
import { ContentTabs } from '@features/utr-modal/ContentTabs';
import { ChartComponent } from '@features/utr-modal/components/chart';
import { CollapsibleQuestionInfo } from '@features/utr-modal/CollapsibleQuestionInfo';
import { AdditionalInfo } from '@features/utr-modal/AdditionalInfo';
import { DataTable } from '@features/utr-modal/DataTable';
import { UniversalTrackerBase, UtrValueType } from '@g17eco/types/universalTracker';
import { ValueListPlain } from '@g17eco/types/valueList';
import { TOTAL_OPTION } from '@constants/utr';
import { UniversalTrackerModalShowAs, UniversalTrackerModalTab } from '@g17eco/types/universalTrackerModal';
import UniversalTrackerClassInterface from '../../../model/UniversalTrackerClassInterface';

interface UtrModalContainerProps {
  utr: UniversalTrackerClassInterface;
  utrvs: UniversalTrackerModalServiceUtrv[];
  firstValueListCode?: string;
  valueList?: ValueListPlain;
}

export const UtrModalBody = (props: UtrModalContainerProps) => {
  const { utr, utrvs, firstValueListCode: selectedColumnCode, valueList } = props;

  const getColumnName = () => {
    if (!selectedColumnCode) {
      return;
    }
    if (utr.getValueType() === UtrValueType.NumericValueList && valueList) {
      return selectedColumnCode === TOTAL_OPTION.value
        ? TOTAL_OPTION.label
        : valueList.options.find((op) => op.code === selectedColumnCode)?.name;
    }
    return utr.getValueValidation().table?.columns.find((col) => col.code === selectedColumnCode)?.name;
  };

  const columnName = getColumnName();

  const utrPlain: UniversalTrackerBase = utr.getRaw();
  const canDrawChart = canAddTarget(utrPlain);

  const [activeTab, setActiveTab] = useState<UniversalTrackerModalTab['navId']>(UniversalTrackerModalShowAs.Chart);

  const viewTabs: UniversalTrackerModalTab[] = [
    {
      navId: UniversalTrackerModalShowAs.Chart,
      disabled: !canDrawChart,
      name: 'Chart',
      component: <ChartComponent utr={utr} utrvs={utrvs} selectedColumnCode={selectedColumnCode} />,
    },
    {
      navId: UniversalTrackerModalShowAs.Table,
      name: 'Table',
      component: (
        <DashboardRow>
          <DataTable selectedColumnCode={selectedColumnCode} utr={utr} utrvs={utrvs} hideProvenance />
        </DashboardRow>
      ),
    },
  ];

  return (
    <Dashboard className='utr-modal-container'>
      <CollapsibleQuestionInfo utr={utr} />

      <DashboardRow mb={0}>
        <div className='w-100'>
          <div className='w-100 d-flex flex-wrap justify-content-between align-items-center'>
            <ViewRadioButtons tabs={viewTabs} activeTab={activeTab} handleChangeTab={setActiveTab} />
          </div>
          {columnName ? (
            <div className='mt-3'>
              <strong>Metric:</strong> {columnName}
            </div>
          ) : null}
        </div>
      </DashboardRow>

      <ContentTabs tabs={viewTabs} activeTab={activeTab} />

      <AdditionalInfo utrPlain={utrPlain} />
    </Dashboard>
  );
};
